import { makeAutoObservable, runInAction } from 'mobx';
import { SortType } from '../../../../constants/tool-order';
import { formatEUR, exportMultipleSheetExcel, roundSalesQuantity } from '../../../../constants/tool.fn';
import { applyClientTableSort, DISH_SALES_SORT_COLUMN_TYPES } from '../dish-data-table-sort.utils';
import { dateFormat } from '../../../../constants/utils';
import i18n from '../../../../i18n';
import { LOCAL_STORAGE_KEYS } from '../../../../modules/cache/local-storage.constant';
import LSM from '../../../../modules/cache/tool-local-storage';
import apiManage from '../../../../request';
import {
    DishCategoryItem,
    RestaurantMenuSalesAnalysisMode,
    RestaurantMenuSalesAnalysisType,
} from '../../../../request/api/data-analysis';
import { FetchCancelGate } from '../../../../utils/fetch-cancel-gate';
import dataAnalysisStore from '../../data-analysis-state';
import { DishDataSelectOption, MenuSalesRow } from './dish-data-shared.types';

/** 菜品销售分析维度。 */
export type DishAnalysisType = 'menu' | 'category' | 'dish';

/** 菜品销售字段 key。 */
export type DishColumnKey =
    | 'menu_name'
    | 'category_name'
    | 'item_name1'
    | 'item_name2'
    | 'total_quantity'
    | 'total_amount'
    | 'return_rate'
    | 'sales_ratio';

/** 不同分析维度下的默认列。 */
export const DEFAULT_DISH_COLUMN_KEYS_BY_TYPE: Record<DishAnalysisType, DishColumnKey[]> = {
    menu: ['menu_name', 'total_quantity', 'total_amount', 'sales_ratio'],
    category: ['menu_name', 'category_name', 'total_quantity', 'total_amount', 'sales_ratio'],
    dish: ['menu_name', 'category_name', 'item_name1', 'item_name2', 'total_quantity', 'total_amount', 'return_rate', 'sales_ratio'],
};

/**
 * 菜品销售分析状态。
 */
export class DishSalesState {
    /** 加载态。 */
    loading = false;

    /** 错误文案内容。 */
    errorMessage = '';

    /** 列表数据。 */
    list: MenuSalesRow[] = [];

    /** 当前分析维度。 */
    analysisType: DishAnalysisType = 'menu';

    /** 一级菜单选中值。 */
    mainGroupId = '-1';

    /** 二级菜单选中值。 */
    secondMenuId = '-1';

    /** 一级菜单选项。 */
    mainGroups: DishDataSelectOption[] = [{ value: '-1', label: 'business_analysis_page_text_all' }];

    /** 一级菜单原始数据。 */
    mainGroupsOriginData: DishCategoryItem[] = [];

    /** 二级菜单选项。 */
    secondMenuOptions: DishDataSelectOption[] = [{ value: '-1', label: 'business_analysis_page_text_all' }];

    /** 当前餐厅 string_id。 */
    currentRestaurantStringId = '';

    /** 搜索关键词。 */
    searchKeyword = '';

    /** 是否为多选模式。 */
    multiMode = false;

    /** 当前选中的行 key。 */
    selectedKeys: string[] = [];

    /** 当前可见列。 */
    visibleColumnKeys: DishColumnKey[] = [...DEFAULT_DISH_COLUMN_KEYS_BY_TYPE.menu];

    /** 表格前端排序状态。 */
    sortKeyValue: SortType = {};

    /** 列表请求取消门闩（日期/筛选快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    /** 菜单筛选请求序号，用于忽略过期响应。 */
    private filterRequestSeq = 0;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * 设置分析维度。
     * @param value - 分析维度
     */
    setAnalysisType(value: DishAnalysisType) {
        this.analysisType = value;
        this.mainGroupId = '-1';
        this.secondMenuId = '-1';
        this.secondMenuOptions = [{ value: '-1', label: 'business_analysis_page_text_all' }];
        this.visibleColumnKeys = [...DEFAULT_DISH_COLUMN_KEYS_BY_TYPE[value]];
        this.selectedKeys = [];
        this.sortKeyValue = {};
    }

    /**
     * 设置一级菜单。
     * @param value - 一级菜单值
     */
    setMainGroupId(value: string) {
        this.mainGroupId = value;
        this.secondMenuOptions = this.createSecondMenuOptions(value);
        this.secondMenuId = '-1';
        this.selectedKeys = [];
    }

    /**
     * 设置二级菜单。
     * @param value - 二级菜单值
     */
    setSecondMenuId(value: string) {
        this.secondMenuId = value;
        this.selectedKeys = [];
    }

    /**
     * 设置当前餐厅，并在切餐厅时清空餐厅相关筛选。
     * @param value - 当前餐厅 string_id
     */
    setRestaurantStringId(value: string) {
        if (this.currentRestaurantStringId === value) {
            return;
        }
        this.currentRestaurantStringId = value;
        this.searchKeyword = '';
        this.mainGroupId = '-1';
        this.secondMenuId = '-1';
        this.mainGroupsOriginData = [];
        this.mainGroups = [{ value: '-1', label: 'business_analysis_page_text_all' }];
        this.secondMenuOptions = [{ value: '-1', label: 'business_analysis_page_text_all' }];
        this.selectedKeys = [];
    }

    /**
     * 设置搜索关键词。
     * @param value - 搜索关键词
     */
    setSearchKeyword(value: string) {
        this.searchKeyword = value;
    }

    /**
     * 设置表格前端排序。
     * @param value - 排序列与方向
     */
    setSortValue(value: SortType) {
        this.sortKeyValue = value;
    }

    /**
     * 切换多选模式。
     */
    toggleMultiMode() {
        this.multiMode = !this.multiMode;
        if (!this.multiMode) {
            this.selectedKeys = this.selectedKeys.slice(0, 1);
        }
    }

    /**
     * 设置当前可见列。
     * @param nextKeys - 新的列 key 列表
     */
    setVisibleColumnKeys(nextKeys: DishColumnKey[]) {
        this.visibleColumnKeys = nextKeys;
    }

    /**
     * 用表格选中行同步内部 key 列表。
     * @param selectedRows - 选中行对象
     */
    setSelectedRows(selectedRows: MenuSalesRow[]) {
        this.selectedKeys = selectedRows.map((item) => item.rowKey);
    }

    /**
     * 切换单行的选中状态，用于无复选框的行点击交互。
     * @param row - 当前点击的行
     */
    toggleRowSelection(row: MenuSalesRow) {
        /** 当前行是否已被选中。 */
        const isSelected = this.selectedKeys.includes(row.rowKey);
        this.selectedKeys = isSelected
            ? this.selectedKeys.filter((item) => item !== row.rowKey)
            : [...this.selectedKeys, row.rowKey];
    }

    /**
     * 全选当前过滤后列表。
     */
    selectAllFilteredRows() {
        /** 当前过滤结果的 key 列表。 */
        const filteredKeys = this.filteredRows.map((row) => row.rowKey);
        this.selectedKeys = Array.from(new Set([...this.selectedKeys, ...filteredKeys]));
    }

    /**
     * 切换“全选当前过滤结果”状态。
     */
    toggleSelectAllFilteredRows() {
        if (this.isAllFilteredSelected) {
            /** 当前过滤结果的 key 集合。 */
            const filteredKeySet = new Set(this.filteredRows.map((row) => row.rowKey));
            this.selectedKeys = this.selectedKeys.filter((key) => !filteredKeySet.has(key));
            return;
        }
        this.selectAllFilteredRows();
    }

    /**
     * 清空当前选择态。
     */
    clearSelection() {
        this.selectedKeys = [];
    }

    /**
     * 拉取一级/二级菜单筛选项。
     * @param stringId - 餐厅 string_id
     */
    async fetchFilters(stringId: string) {
        /** 当前请求序号。 */
        const requestId = ++this.filterRequestSeq;
        const response = await apiManage.get_data_analysis_res_dish_Category(stringId);
        runInAction(() => {
            if (requestId !== this.filterRequestSeq || this.currentRestaurantStringId !== stringId) {
                return;
            }
            if (response?.error || !Array.isArray(response)) {
                this.mainGroupsOriginData = [];
                this.mainGroups = [{ value: '-1', label: 'business_analysis_page_text_all' }];
                this.secondMenuOptions = [{ value: '-1', label: 'business_analysis_page_text_all' }];
                return;
            }
            this.mainGroupsOriginData = response;
            this.mainGroups = [
                { value: '-1', label: 'business_analysis_page_text_all' },
                ...response.map((item) => ({
                    value: `${item.main_group_id ?? ''}`,
                    label: item.main_group_name ?? '',
                })),
            ];
            this.secondMenuOptions = this.createSecondMenuOptions(this.mainGroupId);
        });
    }

    /**
     * 拉取菜品销售列表。
     * @param stringId - 餐厅 string_id
     */
    async fetchData(stringId: string) {
        if (this.currentRestaurantStringId !== stringId) {
            this.setRestaurantStringId(stringId);
        }
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.loading = true;
        this.errorMessage = '';
        this.list = [];
        this.selectedKeys = [];
        this.sortKeyValue = {};
        try {
            const response = await apiManage.get_menu_sales_analysis(
                stringId,
                this.buildMenuSalesParams(),
                { cancelToken }
            );
            runInAction(() => {
                if (!this.listFetchGate.isLatest(seq)) {
                    return;
                }
                if (response?.error || !Array.isArray(response.list)) {
                    this.list = [];
                    this.errorMessage = i18n.t('business_analysis_page_text_restaurant_data_not_synchronized');
                    return;
                }
                /** 列表行：补充 rowKey；不再写入 `totalMaxRate`（旧版进度条已移除）。 */
                this.list = response.list.map((item, index) => {
                    /** 销售数量：接口可能为小数，展示/导出/对比统一四舍五入为整数 */
                    const roundedQuantity = roundSalesQuantity(item.total_quantity);
                    return {
                        ...item,
                        total_quantity: roundedQuantity,
                        sum_quantity: roundedQuantity,
                        sum_price: item.total_amount,
                        rowKey: `dish-${index}-${item.id}-${item.menu_name ?? item.category_name ?? item.item_name1 ?? ''}`,
                    };
                });
                this.syncSelection();
            });
        } finally {
            runInAction(() => {
                this.listFetchGate.endLoading(seq, (value) => {
                    this.loading = value;
                });
            });
        }
    }

    /**
     * 是否按当前勾选行导出（无勾选时导出筛选后全部行）。
     * @returns 为 true 时导出 `selectedRows`
     */
    get exportUsesSelectedRows() {
        return this.selectedKeys.length > 0;
    }

    /**
     * 参与导出的行：有勾选则导出勾选行，否则导出当前筛选后全部行。
     * @returns 导出行列表
     */
    get rowsForExport() {
        return this.exportUsesSelectedRows ? this.selectedRows : this.filteredRows;
    }

    /**
     * 导出菜品销售列表（勾选行或筛选后全部行）。
     */
    exportData() {
        /** 默认餐厅信息。 */
        const defaultRestaurant = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT) || {};
        /** 待导出行。 */
        const exportRows = this.rowsForExport;
        /** 表格导出内容。 */
        const sheetData = [{
            sheetName: i18n.t('business_analysis_page_dishes_sales_analysis'),
            data: [[
                i18n.t('business_analysis_page_text_menu_name'),
                i18n.t('business_analysis_page_text_menu_category'),
                i18n.t('business_analysis_page_dishes_name_one'),
                i18n.t('business_analysis_page_dishes_name_two'),
                i18n.t('business_analysis_page_text_sales_quantity'),
                i18n.t('business_analysis_page_text_sales_total'),
                i18n.t('business_analysis_page_text_return_rate'),
                i18n.t('business_analysis_page_text_sales_ratio'),
            ]].concat(exportRows.map((item) => [
                item.menu_name ?? '',
                item.category_name ?? '',
                item.item_name1 ?? '',
                item.item_name2 ?? '',
                String(item.total_quantity ?? 0),
                formatEUR(Number(item.total_amount ?? 0)),
                item.return_rate != null ? `${item.return_rate}%` : '',
                item.sales_ratio != null ? `${item.sales_ratio}%` : '',
            ])),
            wpx: [{ wpx: 160 }, { wpx: 160 }, { wpx: 180 }, { wpx: 180 }, { wpx: 120 }, { wpx: 140 }, { wpx: 120 }, { wpx: 120 }],
        }];
        /** 导出开始时间。 */
        const startText = dateFormat(dataAnalysisStore.startDate, 'YYYY-MM-DD HH-mm');
        /** 导出结束时间。 */
        const endText = dateFormat(dataAnalysisStore.endDate, 'YYYY-MM-DD HH-mm');
        exportMultipleSheetExcel(
            `${i18n.t('business_analysis_page_dishes_sales_analysis')} ${defaultRestaurant.nickname ?? ''} ${startText}-${endText}`,
            sheetData
        );
    }

    /**
     * 过滤后的行列表。
     * @returns 过滤后的列表
     */
    get filteredRows() {
        /** 标准化后的搜索词。 */
        const normalizedKeyword = this.searchKeyword.trim().toLowerCase();
        /** 搜索过滤后的行。 */
        const keywordFilteredRows = !normalizedKeyword
            ? this.list
            : this.list.filter((row) => [
                row.menu_name,
                row.category_name,
                row.item_name1,
                row.item_name2,
            ].some((value) => `${value ?? ''}`.toLowerCase().includes(normalizedKeyword)));
        return applyClientTableSort(keywordFilteredRows, this.sortKeyValue, DISH_SALES_SORT_COLUMN_TYPES);
    }

    /**
     * 当前选中的行对象。
     * @returns 选中行
     */
    get selectedRows() {
        return this.list.filter((row) => this.selectedKeys.includes(row.rowKey));
    }

    /**
     * 是否允许打开对比弹窗。
     * @returns 是否允许对比
     */
    get canCompare() {
        return this.selectedRows.length >= 2;
    }

    /**
     * 当前过滤结果是否已经全部选中。
     * @returns 是否全选
     */
    get isAllFilteredSelected() {
        return this.filteredRows.length > 0
            && this.filteredRows.every((row) => this.selectedKeys.includes(row.rowKey));
    }

    /**
     * 构建二级菜单选项。
     * @param mainGroupId - 一级菜单值
     * @returns 二级菜单选项
     */
    private createSecondMenuOptions(mainGroupId: string): DishDataSelectOption[] {
        if (mainGroupId === '-1') {
            return [{ value: '-1', label: 'business_analysis_page_text_all' }];
        }
        /** 当前一级菜单项。 */
        const currentGroup = this.mainGroupsOriginData.find((item) => `${item.main_group_id ?? ''}` === mainGroupId);
        /** 当前一级菜单下的二级菜单列表。 */
        const menuItemSlu = currentGroup?.menu_item_slu ?? [];
        return [
            { value: '-1', label: 'business_analysis_page_text_all' },
            ...menuItemSlu.map((item) => ({
                value: `${item.dmi_slu_id ?? ''}`,
                label: item.dmi_slu_name ?? '',
            })),
        ];
    }

    /**
     * 构建 menu-sales-analysis 入参。
     * @returns 接口参数
     */
    private buildMenuSalesParams() {
        /** 当前日期模式（与营业分析全局时间筛选一致）。 */
        const periodMode = dataAnalysisStore.getPeriodMode();
        /** 接口 mode。 */
        const mode = periodMode === 'today'
            ? RestaurantMenuSalesAnalysisMode.Today
            : periodMode === 'this_week'
                ? RestaurantMenuSalesAnalysisMode.Week
                : periodMode === 'this_month'
                    ? RestaurantMenuSalesAnalysisMode.Month
                    : RestaurantMenuSalesAnalysisMode.Custom;
        /** 接口 type。 */
        const type = this.analysisType === 'menu'
            ? RestaurantMenuSalesAnalysisType.Menu
            : this.analysisType === 'category'
                ? RestaurantMenuSalesAnalysisType.Category
                : RestaurantMenuSalesAnalysisType.Item;
        return {
            type,
            mode,
            start_time: dataAnalysisStore.startDate,
            end_time: dataAnalysisStore.endDate,
            menu_id: type !== RestaurantMenuSalesAnalysisType.Menu && this.mainGroupId !== '-1' ? this.mainGroupId : undefined,
            category_id: type === RestaurantMenuSalesAnalysisType.Item && this.secondMenuId !== '-1' ? this.secondMenuId : undefined,
        };
    }

    /**
     * 根据当前过滤结果修正选择态。
     */
    private syncSelection() {
        this.selectedKeys = this.selectedKeys.filter((key) => this.list.some((row) => row.rowKey === key));
    }
}

export default DishSalesState;
